import { Injectable, OnDestroy } from '@angular/core';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { TranslateService } from '@ngx-translate/core';
import { IndexDBService } from '../../common/indexDB.service';
import { ITableData } from '../../../models/reports/ITableData';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IData } from '../../../models/reports/IData';
import {
  EstimateData,
  EstimateMetaData, LaborMetaData, Material, MinimumProjectStandardMarginRequest, MinimumProjectStandardMarginResponse, MiscMetaData, OtherMetaData, OtPwrSalesOfficeCollection,
  OverTimePWRAdjustment, ProductPromotionModel, SubSystem
} from '../../../models/dtos';
import { SubmittalReportInfo } from '../../../models/common/EstimateOverview';
import { firstValueFrom } from 'rxjs';
import { ReportsAPIService } from '../../api/reports-api.service';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';
import { CommonMetaDataService } from '../../common-meta-data.service';

@Injectable()
export class EstimateOverviewReportService implements OnDestroy {
  private isOtAndPwrTextAvailable: boolean = false;
  private overTimeText: string = CONSTANTS.EMPTY_STRING;
  private prevailingText: string = CONSTANTS.EMPTY_STRING;
  private userDefinedQuantity: number = CONSTANTS.ZERO;
  private costOverride: string = CONSTANTS.NO;
  private laborMetaData: LaborMetaData;
  private estimateMetaData: EstimateMetaData;
  private otherMetaData: OtherMetaData;
  private estimateData: EstimateData;

  constructor(
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    private readonly indexdb: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly reportAPIService: ReportsAPIService,
    private readonly commonMetaDataService: CommonMetaDataService
  ) { }

  async generateReport(reportId: ReportList,
    pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const translatedReportName = this.translate.instant(reportName);
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: translatedReportName,
      reportTitle: translatedReportName,
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: await this.generateTableData(),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };
    this.reportsCommonService.setSelectedReport(reportData);
  }

  private async generateTableData(): Promise<ITableData[]> {
    this.laborMetaData = this.commonDataService.getLaborMetaData();
    this.estimateMetaData = this.commonDataService.getEstimateMetaData();
    this.otherMetaData = this.commonDataService.getOtherMetaData();
    this.estimateData = this.commonDataService.getEstimateOpenResponse().estimateData;
    const tableData: ITableData[] = [];

    // Gather labor overhead data
    await this.getLaborOverHeads();

    // Gather special pricing data
    await this.traverseAreasForSpecialPricing();

    // Build the five tables
    const estimateTable = await this.buildEstimateTable();
    const opportunityTable = this.buildOpportunityTable();
    const customerTable = this.buildCustomerTable();
    const specialPricingTable = this.buildSpecialPricingTable();
    const promotionTable = this.buildPromotionTable();

    tableData.push(estimateTable);
    if (this.commonDataService.isCollaborationEstimate()) {
      tableData.push(this.buildCollaborativeUsersTable());
    }
    tableData.push(opportunityTable);
    tableData.push(customerTable);
    tableData.push(specialPricingTable);
    tableData.push(promotionTable);

    return tableData;
  }

  private buildOpportunityTable(): ITableData {
    const reportInfo: SubmittalReportInfo = {} as SubmittalReportInfo;
    const opportunityId = this.estimateMetaData.estimateSettings.opportunityId;

    if (opportunityId?.trim()) {
      reportInfo.linked = CONSTANTS.YES;
      reportInfo.opportunityId = opportunityId;
      reportInfo.opportunityName = this.commonDataService.getOpportunityRelationshipData().opportunityName ?? CONSTANTS.EMPTY_STRING;
    }
    const titleRow = this.createFourColumnTitleRow(this.translate.instant('OPPORTUNITY'));
    const dataRows: IData[] = [];

    dataRows.push( 
      this.createFourColumnRow(
      this.translate.instant('LINKED_OPPORTUNITY'),
      reportInfo.linked ?? CONSTANTS.NO,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
      ),
      this.createFourColumnRow(
      this.translate.instant('OPPORTUNITY_ID'),
      reportInfo.opportunityId,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
      ),
      this.createFourColumnRow(
      this.translate.instant('OPPORTUNITY_NAME'),
      reportInfo.opportunityName,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
      )
    );

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  private buildCustomerTable(): ITableData {
    const customerData = this.otherMetaData;
    const customerInformation = customerData.customerInformation;
    const shipToAccount = customerData.shipToAccount;
    const reportInfo: SubmittalReportInfo = {} as SubmittalReportInfo;

    if (customerInformation) {
      if (customerInformation.endUserName?.trim()) {
        reportInfo.endUserName = customerInformation.endUserName;
      }
      if (customerInformation.customerName?.trim()) {
        reportInfo.sapName = customerInformation.customerName;
      }
      if (customerInformation.customerID?.trim()) {
        reportInfo.sapNumber = customerInformation.customerID;
      } else if (customerInformation.customerSalesforceId?.trim()) {
        reportInfo.sapNumber = customerInformation.customerSalesforceId;
      }
    }

    if (shipToAccount) {
      if (shipToAccount.customerName?.trim()) {
        reportInfo.siteName = shipToAccount.customerName;
      }
      if (shipToAccount.customerSalesforceId?.trim()) {
        reportInfo.siteId = shipToAccount.customerSalesforceId;
      }
    }

    const titleRow = this.createFourColumnTitleRow(this.translate.instant('CUSTOMER'));
    const dataRows: IData[] = [];

  
    dataRows.push(
      // Header row
      this.createFourColumnRow(
      this.translate.instant('SOLD_TO'),
      CONSTANTS.EMPTY_STRING,
      this.translate.instant('SHIP_TO_LOCATION'),
      CONSTANTS.EMPTY_STRING
    ),
      // Data rows
      this.createFourColumnRow(
      this.translate.instant('SAP_NUMBER'),
      reportInfo.sapNumber,
      this.translate.instant('SITE_ID'),
      reportInfo.siteId,
      CONSTANTS.TWO
    ),
      this.createFourColumnRow(
      this.translate.instant('SAP_NAME'),
      reportInfo.sapName,
      this.translate.instant('SITE_NAME'),
      reportInfo.siteName,
      CONSTANTS.TWO
    ),
      this.createFourColumnRow(
      this.translate.instant('END_USER_NAME'),
      reportInfo.endUserName,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    )
    );

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  async buildEstimateTable(): Promise<ITableData> {
    const estimateInfo: SubmittalReportInfo = {} as SubmittalReportInfo;
    const estimateMetaData = this.estimateMetaData;
    const estimateSettings = estimateMetaData.estimateSettings;
    const estimateTabIsFixedPriceSelected = estimateMetaData.estimateTab.isFixedPriceSelected;
    const soldAsSellPrice = estimateMetaData.estimateTab.soldASSellPrice;
    const laborRates = this.laborMetaData.salesOfficeLaborRates.find(segId => segId.segmentId);
    const isPriceRecommenderUsed = this.otherMetaData.priceRecommendations.isPriceRecommenderUsed;


    const metaDataResponse = this.commonMetaDataService.getMetaData();
    if(!metaDataResponse) {
      return null;
    }
    const businessAndSalesOffice = metaDataResponse.businessesAndSalesOffices.find(
      x => x.businessUnitId === estimateSettings.businessUnitId && x.id === estimateSettings.salesOfficeId
    );
    estimateInfo.business = businessAndSalesOffice?.businessUnitName ?? CONSTANTS.EMPTY_STRING;
    estimateInfo.salesOffice = businessAndSalesOffice?.branchName_City ?? CONSTANTS.EMPTY_STRING;

    const businessHeirarchy = metaDataResponse.subSegments.find(
      x => x.subSegmentId === estimateSettings.subSegmentId && x.businessUnitId === estimateSettings.businessUnitId
    );
    estimateInfo.businessHierarchy = businessHeirarchy?.subSegment ?? CONSTANTS.EMPTY_STRING;

    const segment = metaDataResponse.segments.find(s => s.zweigId === this.estimateData.profitCenterId);
    estimateInfo.segment = segment?.zweigName ?? CONSTANTS.EMPTY_STRING;

    const installType = metaDataResponse.installTypes.find(x => x.id === estimateSettings.installTypeID);
    estimateInfo.installType = installType?.description ?? CONSTANTS.EMPTY_STRING;

    const taxType = metaDataResponse.taxTypes.find(x => x.id === estimateSettings.taxTypeID);
    estimateInfo.tax = taxType?.description ?? CONSTANTS.EMPTY_STRING;



    // TODO: Implement PPM Estimate logic
    if (estimateTabIsFixedPriceSelected === null || estimateTabIsFixedPriceSelected === undefined) {
      estimateInfo.method = this.translate.instant('LINE_ITEM');
    } else {
      estimateInfo.method = estimateTabIsFixedPriceSelected
        ? this.translate.instant('FIXED_PRICE') : this.translate.instant('FIXED_MARGIN');
    }

    estimateInfo.type = this.getEstimateType();
    estimateInfo.verticalMarket = this.getVerticalMarketName();

    if (this.estimateData.business === CONSTANTS.BUSINESS_FIRE) {
      estimateInfo.isFireEstimate = true;

      if (estimateSettings.usePL) {
        estimateInfo.isLeadRateSelected = true;
      }

      if (!this.commonDataService.globalPPMmode) {

        const minimumMargin = await this.getMinimumProjectStandardMargin(
          this.estimateData.cultureID,
          estimateSettings.verticalMarketId,
          soldAsSellPrice,
          laborRates?.segmentId ?? CONSTANTS.ZERO,
          estimateSettings.subSegmentId,
          estimateSettings.systemType
        );

        if (minimumMargin > CONSTANTS.ZERO) {
          estimateSettings.isSystemTypeVisible = true;
        }
      }

      estimateInfo.isPriceRecommenderUsed = isPriceRecommenderUsed ? CONSTANTS.YES : CONSTANTS.NO;
    }

    const titleRow = this.createFourColumnTitleRow(this.translate.instant('ESTIMATE'));
    const dataRows: IData[] = [];

    dataRows.push(
      this.createFourColumnRow(
      this.translate.instant('BUSINESS_UNIT'),
      estimateInfo.business,
      this.translate.instant('METHOD'),
      estimateInfo.method
    ),
      this.createFourColumnRow(
      this.translate.instant('SALES_OFFICE'),
      estimateInfo.salesOffice,
      this.translate.instant('INSTALL_TYPE'),
      estimateInfo.installType
    ),
    this.createFourColumnRow(
      this.translate.instant('SEGMENT'),
      estimateInfo.segment,
      CONSTANTS.OVERTIME,
      this.overTimeText
    ),
    this.createFourColumnRow(
      this.translate.instant('BUSINESS_HIERARCHY'),
      estimateInfo.businessHierarchy,
      CONSTANTS.PREVAILING_WAGE,
      this.prevailingText
    ),
    this.createFourColumnRow(
      this.translate.instant('TYPE'),
      estimateInfo.type,
      this.translate.instant('TAX'),
      estimateInfo.tax
    ),
    this.createFourColumnRow(
      this.translate.instant('SUBMITTED'),
      this.estimateData?.sapSubmissionDate?.toString() ?? CONSTANTS.EMPTY_STRING,
      estimateInfo.isFireEstimate ? this.translate.instant('PM_RATE') : CONSTANTS.EMPTY_STRING,
      estimateInfo.isFireEstimate ? this.translate.instant('PROJECT_MANAGER_RATE') : CONSTANTS.EMPTY_STRING
    ),
    this.createFourColumnRow(
      this.translate.instant('VERTICAL_MARKET'),
      estimateInfo.verticalMarket,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    )
    );

    if (estimateSettings.isSystemTypeVisible) {
      estimateInfo.systemType = estimateSettings.systemType === CONSTANTS.ONE
        ? this.translate.instant('SIEMENS')
        : this.translate.instant('THIRD_PARTY');
      dataRows.push(this.createFourColumnRow(
        this.translate.instant('SYSTEM_TYPE'),
        estimateInfo.systemType,
        CONSTANTS.EMPTY_STRING,
        CONSTANTS.EMPTY_STRING
      ));
    }

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  private buildCollaborativeUsersTable(): ITableData {
    const titleRow = this.createFourColumnTitleRow(this.translate.instant('COLLABORATIVE_USERS'));
    const dataRows: IData[] = [];

    const collaboratorData = this.commonDataService.getCollaboratorData() ?? [];
    const ownerIndex = collaboratorData.findIndex(collaborator =>
      collaborator.roleId === CONSTANTS.COLLABORATION_OWNER_ROLE_ID
    );

    const owner = ownerIndex >= CONSTANTS.ZERO ? collaboratorData[ownerIndex] : null;
    const ownerName = this.getCollaboratorDisplayName(owner) || this.estimateData?.owner || CONSTANTS.EMPTY_STRING;
    const collaborators = collaboratorData.filter((_, index) => index !== ownerIndex);

    const entries: Array<{ label: string; value: string }> = [];
    if (ownerName.trim()) {
      entries.push({
        label: this.translate.instant('OWNER'),
        value: ownerName
      });
    }

    for (const collaborator of collaborators) {
      const collaboratorName = this.getCollaboratorDisplayName(collaborator);
      if (!collaboratorName.trim()) {
        continue;
      }

      entries.push({
        label: this.translate.instant('COLLABORATORS'),
        value: collaboratorName
      });
    }

    for (let i = CONSTANTS.ZERO; i < entries.length; i += CONSTANTS.TWO) {
      const left = entries[i];
      const right = entries[i + CONSTANTS.ONE];

      dataRows.push(this.createFourColumnRow(
        left?.label ?? CONSTANTS.EMPTY_STRING,
        left?.value ?? CONSTANTS.EMPTY_STRING,
        right?.label ?? CONSTANTS.EMPTY_STRING,
        right?.value ?? CONSTANTS.EMPTY_STRING
      ));
    }

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  private buildSpecialPricingTable(): ITableData {
    const titleRow = this.createFourColumnTitleRow(this.translate.instant('SPECIAL_PRICING'));
    const dataRows: IData[] = [];

    const userDefinedValue = this.userDefinedQuantity > CONSTANTS.ZERO ? CONSTANTS.YES : CONSTANTS.NO;
    dataRows.push(this.createFourColumnRow(
      CONSTANTS.COST_OVERRIDES,
      this.costOverride,
      CONSTANTS.USER_DEFINED,
      userDefinedValue
    ));

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  private buildPromotionTable(): ITableData {
    const estimateMetaData: EstimateMetaData = this.estimateMetaData;
    const subsidy = estimateMetaData.estimateTab.subsidy;
    const productpromotion: ProductPromotionModel[] = estimateMetaData.estimateSettings?.productPromotionList || [];

    const reportInfo: SubmittalReportInfo = {} as SubmittalReportInfo;


    let hasProductPromotion = false;

    for (const item of productpromotion) {
      const discount = item.discount.toString();
      switch (item.affectedMaterial) {
        case 'M': reportInfo.massNotification = discount; hasProductPromotion = true; break;
        case 'V': reportInfo.vesdaMaterial = discount; hasProductPromotion = true; break;
        case 'S': reportInfo.siemensMaterial = discount; hasProductPromotion = true; break;
        case 'P': reportInfo.sipassMaterial = discount; hasProductPromotion = true; break;
      }
    }

    reportInfo.productPromotion = hasProductPromotion ? CONSTANTS.YES : CONSTANTS.NO;

    if (subsidy > CONSTANTS.ZERO) {
      reportInfo.subsidyAmount = subsidy.toString();
      reportInfo.showSubsidy = true;
    }

    const titleRow = this.createFourColumnTitleRow(this.translate.instant('PROMOTION'));
    const dataRows: IData[] = [];

    dataRows.push(
      this.createFourColumnRow(
      this.translate.instant('PRODUCT_PROMOTION'),
      reportInfo.productPromotion,
      reportInfo.showSubsidy ? this.translate.instant('SUBSIDY') : CONSTANTS.EMPTY_STRING,
      reportInfo.subsidyAmount
    ),
    this.createFourColumnRow(
      CONSTANTS.MASS_NOTIFICATION,
      reportInfo.massNotification === undefined ? CONSTANTS.ZERO_PERCENT : reportInfo.massNotification + CONSTANTS.PERCENT,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    ),
    this.createFourColumnRow(
      CONSTANTS.SIEMENS_MATERIAL,
      reportInfo.siemensMaterial === undefined ? CONSTANTS.ZERO_PERCENT : reportInfo.siemensMaterial + CONSTANTS.PERCENT,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    ),
    this.createFourColumnRow(
      CONSTANTS.VESDA_MATERIAL,
      reportInfo.vesdaMaterial === undefined ? CONSTANTS.ZERO_PERCENT : reportInfo.vesdaMaterial + CONSTANTS.PERCENT,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    ),
    this.createFourColumnRow(
      CONSTANTS.SIPASS_MATERIAL,
      reportInfo.sipassMaterial === undefined ? CONSTANTS.ZERO_PERCENT : reportInfo.sipassMaterial + CONSTANTS.PERCENT,
      CONSTANTS.EMPTY_STRING,
      CONSTANTS.EMPTY_STRING
    )
    );

    return {
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: false
    };
  }

  private async traverseAreasForSpecialPricing(): Promise<void> {
    this.userDefinedQuantity = CONSTANTS.ZERO;
    // Default to NO; set to YES if any material has cost override
    this.costOverride = CONSTANTS.NO;

    const materialData = await this.indexdb.getAllMaterialData();
    const materialsCollection = materialData.materialsCollection;

    for (const material of Object.values(materialsCollection)) {
      if (!material.systemChildrenCollection?.length) { continue; }

      // Walk system children and apply flags
      for (const systemChild of material.systemChildrenCollection) {
        const sys = systemChild as Material;
        if (sys.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
          const sub = systemChild as SubSystem;
          if (!sub.children?.length) { continue; }
          for (const subChild of sub.children) {
            this.applyMaterialFlags(subChild);
          }
        } else {
          this.applyMaterialFlags(sys);
        }
      }
    }
  }

  private async getLaborOverHeads(): Promise<void> {
    this.isOtAndPwrTextAvailable = false;
    this.overTimeText = this.translate.instant('NO');
    this.prevailingText = this.translate.instant('NO');
    await this.getProjectLevelHours();
  }

  private async getProjectLevelHours(): Promise<void> {
    const laborData = await this.indexdb.getAllLaborData();
    const laborCollection = laborData.laborsCollection;
    for (const labor of Object.values(laborCollection)) {
      const otCollection = labor.stdSalesOfficesOtPwrAdjustmentsCollection;
      if (otCollection) {
        this.addOtRows(otCollection);
        if (this.isOtAndPwrTextAvailable) {
          return;
        }
      }
    }
  }

  private addOtRows(otCollection: OtPwrSalesOfficeCollection): void {
    for (const otPwrSalesOffice of otCollection) {
      const adjustments = otPwrSalesOffice.overTimePWRAdjustments;
      for (const otPwrRow of adjustments) {
        this.assignOTPWRText(otPwrRow);
        if (this.isOtAndPwrTextAvailable) {
          return;
        }
      }
    }
  }

  private assignOTPWRText(otPwrRow: OverTimePWRAdjustment): void {
    if (
      otPwrRow.referenceNumber !== this.translate.instant('STRAIGHT') &&
      otPwrRow.referenceNumber !== this.translate.instant('STRAIGHT_FR')
    ) {
      const info: SubmittalReportInfo = {} as SubmittalReportInfo;

      this.getOTPWRHours(otPwrRow, info);

      if (!this.checkForZeroHours(info)) {
        if (this.checkForZeroOvertime(otPwrRow.referenceNumber)) {
          this.overTimeText = this.translate.instant('YES');
        }

        if (this.checkForZeroPrevailing(otPwrRow.referenceNumber)) {
          this.prevailingText = this.translate.instant('YES');
        }

        this.isOtAndPwrTextAvailable =
          this.overTimeText === this.translate.instant('YES') &&
          this.prevailingText === this.translate.instant('YES');
      }
    }
  }

  private getOTPWRHours(otPwrHours: OverTimePWRAdjustment, info: SubmittalReportInfo): void {
    if (otPwrHours != null) {
      if (otPwrHours.projectManagementLabor !== CONSTANTS.NEGATIVE_NA_VALUE) {
        info.projectManagementHours = otPwrHours.projectManagementLabor;
      }
      if (otPwrHours.engineerLabor !== CONSTANTS.NEGATIVE_NA_VALUE) {
        info.engineerHours = otPwrHours.engineerLabor;
      }
      if (otPwrHours.specialistLabor !== CONSTANTS.NEGATIVE_NA_VALUE) {
        info.specialistHours = otPwrHours.specialistLabor;
      }
      info.mechanicalHours = otPwrHours.mechanicalEngineerLabor;
      info.electricalHours = otPwrHours.electricalEngineerLabor;
    }
  }

  private checkForZeroHours(taskRow: SubmittalReportInfo): boolean {
    return (taskRow.projectManagementHours === CONSTANTS.ZERO || taskRow.projectManagementHours === undefined) &&
      (taskRow.engineerHours === CONSTANTS.ZERO || taskRow.engineerHours === undefined) &&
      (taskRow.specialistHours === CONSTANTS.ZERO || taskRow.specialistHours === undefined) &&
      (taskRow.mechanicalHours === CONSTANTS.ZERO || taskRow.mechanicalHours === undefined) &&
      (taskRow.electricalHours === CONSTANTS.ZERO || taskRow.electricalHours === undefined);
  }

  private checkForZeroOvertime(reference: string): boolean {
    return reference === CONSTANTS.OT_11X_TIME_CASE ||
      reference === CONSTANTS.OT_15X_TIME_CASE ||
      reference === CONSTANTS.OT_2X_TIME_CASE;
  }

  private checkForZeroPrevailing(reference: string): boolean {
    return reference === CONSTANTS.PWR_STRAIGHT_TIME_CASE
      || reference === CONSTANTS.PWR_11X_TIME_CASE
      || reference === CONSTANTS.PWR_15X_TIME_CASE
      || reference === CONSTANTS.PWR_2X_TIME_CASE;
  }

  private async getMinimumProjectStandardMargin(cultureId: number, verticalMarket: number, sellPrice: number, segmentId: number,
    subSegmentId: number, systemOrSalesTypeId: number): Promise<number> {

    const request: MinimumProjectStandardMarginRequest = {
      cultureId,
      verticalMarketId: verticalMarket,
      sellPrice,
      segmentId,
      subSegmentId,
      systemOrSalesTypeId
    };

    const result: MinimumProjectStandardMarginResponse =
      await firstValueFrom(this.reportAPIService.getMinimumProjectStandardMargin(request));

    return result.margin;
  }


  private getVerticalMarketName(): string {
    return this.commonDataService.getReviewObject()?.costReviewSummary?.verticalMarket ?? CONSTANTS.EMPTY_STRING;
  }

  private getEstimateType(): string {
    return this.commonMetaDataService.getMetaData()?.estimateType
            .find((type) => type.id === this.estimateData.estimateTypeId)?.description 
      || CONSTANTS.EMPTY_STRING;
  }

  private createFourColumnRow(label1: string, value1: string | undefined, label2: string, value2: string | undefined, labelHierarchy: number = CONSTANTS.ONE): IData {
    return {
      data: [
        { data: label1, columnspan: CONSTANTS.ONE, hierarchyLevel: labelHierarchy, alignment: CONSTANTS.LEFT_ALIGNMENT },
        {
          data: value1 || CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        { data: label2, columnspan: CONSTANTS.ONE, hierarchyLevel: labelHierarchy, alignment: CONSTANTS.LEFT_ALIGNMENT },
        {
          data: value2 || CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ]
    };
  }

  private createFourColumnTitleRow(titleText: string): IDataItem[] {
    return [
      {
        data: titleText,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
        alignment: CONSTANTS.LEFT_ALIGNMENT
      },
      {
        data: CONSTANTS.EMPTY_STRING,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: CONSTANTS.LEFT_ALIGNMENT
      },
      {
        data: CONSTANTS.EMPTY_STRING,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: CONSTANTS.LEFT_ALIGNMENT
      },
      {
        data: CONSTANTS.EMPTY_STRING,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: CONSTANTS.LEFT_ALIGNMENT
      }
    ];
  }

  private getCollaboratorDisplayName(collaborator: { name?: string; email?: string } | null): string {
    if (!collaborator) {
      return CONSTANTS.EMPTY_STRING;
    }

    return collaborator.name?.trim() || CONSTANTS.EMPTY_STRING;
  }

  private applyMaterialFlags(materialLike: { quantity?: number; costOverride?: boolean; referenceNumber?: string }): void {
    if (materialLike.quantity === CONSTANTS.ZERO) {
      return;
    }
    if (materialLike.costOverride) {
      this.costOverride = CONSTANTS.YES;
    }
    if (materialLike.referenceNumber === CONSTANTS.USER_DEFINED_REFERENCE_NUMBER) {
      this.userDefinedQuantity += materialLike.quantity ?? CONSTANTS.ZERO;
    }
  }

  ngOnDestroy(): void {
    this.isOtAndPwrTextAvailable = false;
    this.overTimeText = CONSTANTS.EMPTY_STRING;
    this.prevailingText = CONSTANTS.EMPTY_STRING;
    this.userDefinedQuantity = CONSTANTS.ZERO;
    this.costOverride = CONSTANTS.NO;
    this.estimateMetaData = {} as EstimateMetaData;
    this.otherMetaData = {} as OtherMetaData;
    this.laborMetaData = {} as LaborMetaData;
  }
}